import { useDebounce } from '@/hooks/useDebounce';
import { useState } from 'react';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { ArrowRightIcon, GraphIcon, PlusIcon } from '@phosphor-icons/react';
import { DateTime, msg, T, useGT, Var } from 'gt-react';
import Page from '@/components/page/Page';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { orpc } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';
import {
  Route as pathlessAuthenticatedRoute,
  requireFeatureFlag,
  requirePermission,
} from '../route';

// These branches render loading, empty, and permission states; splitting them adds indirection.
// fallow-ignore-next-line complexity
function ProjectsPage() {
  const gt = useGT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [canCreate] = useCan(Action.Create, Subject.Project);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const projects = useQuery(
    orpc.projects.list.queryOptions({
      input: { search: debouncedSearch || undefined },
      placeholderData: keepPreviousData,
    }),
  );
  const createProject = useMutation(
    orpc.projects.create.mutationOptions({
      onSuccess: async (project) => {
        setOpen(false);
        setName('');
        setDescription('');
        await queryClient.invalidateQueries({
          queryKey: orpc.projects.list.queryKey({ input: {} }),
        });
        await navigate({
          to: '/projects/$projectId',
          params: { projectId: project.id },
        });
      },
    }),
  );

  return (
    <Page
      title={<T context="projects page title">Projects</T>}
      subtitle={
        <T context="projects page subtitle">
          Files, conversations, and work with the assistant.
        </T>
      }
      isLoading={projects.isPending}
      error={projects.error ?? undefined}
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-3">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={gt('Search projects', {
              context: 'projects search placeholder',
            })}
            className="max-w-md"
          />
          {canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusIcon />
                  <T context="create project button">New project</T>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    <T context="create project dialog title">Create project</T>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={gt('Project name', {
                      context: 'new project name placeholder',
                    })}
                    autoFocus
                  />
                  <Textarea
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder={gt('Description (optional)', {
                      context: 'new project description placeholder',
                    })}
                  />
                </div>
                {createProject.error && (
                  <p role="alert" className="text-sm text-destructive">
                    {createProject.error.message}
                  </p>
                )}
                <DialogFooter>
                  <Button
                    onClick={() =>
                      createProject.mutate({
                        name,
                        description: description || null,
                      })
                    }
                    disabled={!name.trim() || createProject.isPending}
                  >
                    <T context="confirm create project button">Create</T>
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {projects.data?.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex min-h-48 flex-col items-center justify-center gap-2 text-center">
              <GraphIcon size={36} className="text-muted-foreground" />
              <p className="font-medium">
                <T context="empty projects title">No projects yet</T>
              </p>
              <p className="text-sm text-muted-foreground">
                <T context="empty projects description">
                  Create one to upload, browse, and review files with the
                  assistant.
                </T>
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <div className="flex items-center justify-between gap-4 border-b bg-muted/30 px-5 py-3 text-xs font-medium text-muted-foreground">
              <span>
                <T context="projects list heading">Project</T>
              </span>
              <span>
                <T context="projects list order">Recently updated</T>
              </span>
            </div>
            {projects.data?.map((project) => (
              <Link
                key={project.id}
                to="/projects/$projectId"
                params={{ projectId: project.id }}
                className="flex items-center gap-4 border-b px-5 py-5 transition-colors last:border-b-0 hover:bg-muted/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
              >
                <GraphIcon
                  size={24}
                  className="shrink-0 text-muted-foreground"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate font-medium">{project.name}</span>
                  <span className="truncate text-sm text-muted-foreground">
                    {project.description ||
                      gt('Add files to start working with the assistant', {
                        context: 'project without description',
                      })}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    <T context="project file count">
                      <Var>{project.fileCount}</Var> files
                    </T>
                  </span>
                </div>
                <span className="hidden text-sm text-muted-foreground sm:block">
                  <DateTime options={{ dateStyle: 'medium' }}>
                    {project.updatedAt}
                  </DateTime>
                </span>
                <ArrowRightIcon className="shrink-0 text-muted-foreground" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/projects',
  staticData: {
    app: {
      nav: {
        label: msg('Projects', { context: 'sidebar nav label' }),
        icon: <GraphIcon className="h-4 w-4" weight="fill" />,
        group: 'Overview',
        order: 7,
        groupOrder: 1,
      },
      access: {
        featureFlag: 'projects-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Project }],
      },
    },
  },
  component: ProjectsPage,
  loader: () => ({
    crumb: msg('Projects', { context: 'projects breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireFeatureFlag('projects-enabled');
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Project,
    });
  },
});
